import { browserLogger } from './browserLogger';

type DatadogReservedAttributes =
    | 'status'
    | 'host'
    | 'source'
    | 'service'
    | 'message'
    | 'trace_id'
    | 'date'
    | '_id';

type LogContext = {
    [key: string]: unknown;
} & {
    [K in DatadogReservedAttributes]?: never;
};

export const logger = {
    error: logError,
    warning: logWarning,
    info: logInfo,
};

function logError(
    message: string,
    context: LogContext & {
        error?: never;
    },
    error?: unknown,
): void {
    const loggerContextWithError = {
        ...context,
        errorMessage: error instanceof Error ? error.message : undefined,
        error,
    };
    const errorInstance = error instanceof Error ? error : undefined;

    if (process.env.NODE_ENV === 'development') {
        console.error(message, context, error);
    }

    if (isServer()) {
        return void import('./serverLogger').then(({ serverLogger }) => {
            if (errorInstance) {
                serverLogger.error({ ...context, err: errorInstance }, message);
            } else {
                serverLogger.error(loggerContextWithError, message);
            }
        });
    }

    return browserLogger.error(message, loggerContextWithError, errorInstance);
}

function logWarning(message: string, context: LogContext) {
    if (process.env.NODE_ENV === 'development') {
        console.warn(message, context);
    }

    if (isServer()) {
        return void import('./serverLogger').then(({ serverLogger }) => {
            serverLogger.warn(context, message);
        });
    }

    return browserLogger.warn(message, context);
}

function logInfo(message: string, context: LogContext) {
    if (process.env.NODE_ENV === 'development') {
        console.info(message, context);
    }

    if (isServer()) {
        return void import('./serverLogger').then(({ serverLogger }) => {
            serverLogger.info(context, message);
        });
    }

    return browserLogger.info(message, context);
}

function isServer() {
    return typeof window === 'undefined';
}
